import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { switchMap } from 'rxjs';
import {
  OrderApiService,
  OrderResponse,
  OrderTypes,
} from '@nebalkon-workspace/order-api';
import {
  WarehouseResponse,
  WarehouseService,
} from '@nebalkon-workspace/storage-api';
import { formatOrderShortPublicId } from '@nebalkon-workspace/cabinet-core';
import { CabinetButtonComponent } from '@nebalkon-workspace/cabinet-client';
import {
  NotInput,
  NotModal,
  NotSystemNotificationService,
} from '@nebalkon-workspace/ui-kit';
import {
  buildStaffOrderEditPatch,
  orderToStaffOrderEditFormSnapshot,
  StaffOrderEditDialogClose,
  StaffOrderEditDialogData,
} from './staff-order-edit-dialog.types';

@Component({
  selector: 'lib-staff-order-edit-dialog',
  imports: [NotModal, NotInput, CabinetButtonComponent, ReactiveFormsModule, TranslatePipe],
  templateUrl: './staff-order-edit-dialog.component.html',
  styleUrl: './staff-order-edit-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StaffOrderEditDialogComponent implements OnInit {
  readonly data = inject(DIALOG_DATA) as StaffOrderEditDialogData;
  readonly #dialogRef = inject<DialogRef<StaffOrderEditDialogClose>>(DialogRef);
  readonly #fb = inject(FormBuilder);
  readonly #api = inject(OrderApiService);
  readonly #warehouses = inject(WarehouseService);
  readonly #translate = inject(TranslateService);
  readonly #notification = inject(NotSystemNotificationService);
  readonly #destroyRef = inject(DestroyRef);

  protected readonly formatOrderShortPublicId = formatOrderShortPublicId;
  protected readonly OrderTypes = OrderTypes;

  protected readonly order = signal<OrderResponse>(this.data.order);
  protected readonly warehouses = signal<WarehouseResponse[]>([]);
  protected readonly warehousesLoading = signal(true);
  protected readonly saving = signal(false);

  protected readonly form = this.#fb.nonNullable.group({
    pickup_address: [''],
    delivery_address: [''],
    scheduled_at: [''],
    items_description: [''],
    access_description: [''],
    return_to_warehouse: [false],
    warehouse_id: [''],
    reason: [''],
  });

  ngOnInit(): void {
    this.#resetFormFromOrder(this.data.order);
    this.#loadWarehouses();
  }

  protected close(): void {
    this.#dialogRef.close(undefined);
  }

  protected showPickupAddress(): boolean {
    const type = this.order().type;
    return type === OrderTypes.Pickup || type === OrderTypes.Moving;
  }

  protected showDeliveryAddress(): boolean {
    const type = this.order().type;
    return type === OrderTypes.Delivery || type === OrderTypes.Moving;
  }

  protected showItemsDescription(): boolean {
    const type = this.order().type;
    return type === OrderTypes.Pickup || type === OrderTypes.Moving;
  }

  protected showAccessDescription(): boolean {
    return this.order().type === OrderTypes.Delivery;
  }

  protected showReturnToWarehouse(): boolean {
    return this.order().type === OrderTypes.Delivery;
  }

  protected showWarehouseSelect(): boolean {
    const type = this.order().type;
    return type === OrderTypes.Pickup || type === OrderTypes.Delivery;
  }

  protected save(): void {
    if (this.saving()) {
      return;
    }

    const current = this.order();
    const patch = buildStaffOrderEditPatch(current, this.form.getRawValue());
    if (!patch) {
      this.#notification.addToQueue({
        id: Date.now().toString(),
        type: 'info',
        text: [
          this.#translate.instant('admin.storageRequests.editOrderNoChanges'),
        ],
      });
      return;
    }
    if (!patch.reason?.trim()) {
      this.#notification.addToQueue({
        id: Date.now().toString(),
        type: 'error',
        text: [
          this.#translate.instant('admin.storageRequests.changeReasonRequired'),
        ],
      });
      return;
    }

    this.saving.set(true);
    this.#api
      .patchOrder(current.id, patch)
      .pipe(switchMap(() => this.#api.getOrder(current.id)))
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: (updated) => {
          this.saving.set(false);
          this.#dialogRef.close({ kind: 'saved', order: updated });
        },
        error: (err: HttpErrorResponse) => {
          this.saving.set(false);
          this.#toastError(err, 'admin.storageRequests.editOrderError');
        },
      });
  }

  #resetFormFromOrder(order: OrderResponse): void {
    this.form.reset(orderToStaffOrderEditFormSnapshot(order));
  }

  #loadWarehouses(): void {
    this.warehousesLoading.set(true);
    this.#warehouses
      .getAdminWarehouses()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: (response) => {
          this.warehouses.set(response.data ?? []);
          this.warehousesLoading.set(false);
        },
        error: () => {
          this.warehouses.set([]);
          this.warehousesLoading.set(false);
        },
      });
  }

  #toastError(err: HttpErrorResponse, fallbackKey: string): void {
    const message =
      typeof err.error?.error === 'string'
        ? err.error.error
        : this.#translate.instant(fallbackKey);
    this.#notification.addToQueue({
      id: Date.now().toString(),
      type: 'error',
      text: [message],
    });
  }
}
